import React from 'react';

/**
 * WaveBackground Component
 * 
 * Renders the animated wave/blob background used across all pages.
 * This component creates layered wave shapes in corners and sides with
 * subtle floating animations.
 * 
 * The intensity of waves varies by page:
 * - Landing page: Reduced intensity (opacity 0.35) - decreased for subtle glow
 * - Dashboard page: Increased intensity (opacity 0.8)
 * - Default: Normal intensity (opacity 0.7)
 * 
 * @param {string} variant - 'landing', 'dashboard', or 'default'
 */
const WaveBackground = ({ variant = 'default' }) => {
  // Determine wave opacity based on variant
  const opacityClass = {
    landing: 'opacity-wave-landing', // Now 0.35 (reduced in Tailwind config)
    dashboard: 'opacity-wave-dashboard',
    default: 'opacity-wave-common',
  }[variant];

  return (
    <div className="wave-container fixed top-0 left-0 w-full h-full z-wave pointer-events-none overflow-hidden animate-fade-in">
      {/* Mobile simplified background */}
      <div className="md:hidden absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(0, 0, 0, 0) 50%, rgba(255, 255, 255, 0.02) 100%)',
      }} />
      
      {/* Desktop/tablet full background - hidden on mobile */}
      <div className="hidden md:block absolute inset-0">
      {/* Top Corners */}
      <div className={`wave-corner wave-top-left-1 absolute top-0 left-0 w-[1000px] h-[400px] ${opacityClass} will-change-transform animate-blob-float-1`}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.325) 0%, rgba(255, 255, 255, 0.225) 15%, rgba(240, 240, 240, 0.15) 30%, rgba(180, 180, 180, 0.09) 45%, rgba(100, 100, 100, 0.04) 60%, rgba(40, 40, 40, 0.015) 75%, transparent 100%)',
          clipPath: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1000 400\'%3E%3Cpath d=\'M 0,50 Q 150,80 300,60 T 600,70 T 900,55 L 1000,40 L 1000,0 L 0,0 Z\'/%3E%3C/svg%3E")',
          filter: 'blur(1px)',
          boxShadow: '0 0 40px rgba(255, 255, 255, 0.075), inset 0 0 30px rgba(255, 255, 255, 0.04)',
          transform: 'translate(-25%, -30%) rotate(-3deg)',
        }}
      />
      <div className={`wave-corner wave-top-left-2 absolute top-0 left-0 w-[1000px] h-[400px] ${opacityClass} will-change-transform animate-blob-float-1`}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.275) 0%, rgba(250, 250, 250, 0.2) 18%, rgba(220, 220, 220, 0.125) 35%, rgba(150, 150, 150, 0.06) 52%, rgba(60, 60, 60, 0.025) 70%, transparent 100%)',
          clipPath: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1000 400\'%3E%3Cpath d=\'M 0,60 Q 180,100 350,75 T 650,85 T 950,65 L 1000,50 L 1000,0 L 0,0 Z\'/%3E%3C/svg%3E")',
          filter: 'blur(1.5px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 25px rgba(255, 255, 255, 0.03)',
          transform: 'translate(-23%, -28%) rotate(-2deg)',
        }}
      />
      <div className={`wave-corner wave-top-left-3 absolute top-0 left-0 w-[1000px] h-[400px] ${opacityClass} will-change-transform animate-blob-float-1`}
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.225) 0%, rgba(245, 245, 245, 0.15) 22%, rgba(200, 200, 200, 0.09) 42%, rgba(110, 110, 110, 0.04) 62%, rgba(30, 30, 30, 0.015) 82%, transparent 100%)',
          clipPath: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1000 400\'%3E%3Cpath d=\'M 0,70 Q 200,120 400,90 T 700,100 T 1000,75 L 1000,60 L 1000,0 L 0,0 Z\'/%3E%3C/svg%3E")',
          filter: 'blur(2px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translate(-21%, -26%) rotate(-1deg)',
        }}
      />

      <div className={`wave-corner wave-top-right-1 absolute top-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-2`}
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 85% 20%, rgba(255, 255, 255, 0.325) 0%, rgba(255, 255, 255, 0.225) 18%, rgba(240, 240, 240, 0.15) 35%, rgba(180, 180, 180, 0.09) 50%, rgba(100, 100, 100, 0.04) 65%, rgba(40, 40, 40, 0.015) 80%, transparent 100%), radial-gradient(ellipse 60% 80% at 75% 10%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
          filter: 'blur(1px)',
          boxShadow: '0 0 40px rgba(255, 255, 255, 0.075), inset 0 0 30px rgba(255, 255, 255, 0.04)',
          transform: 'translate(25%, -30%)',
        }}
      />
      <div className={`wave-corner wave-top-right-2 absolute top-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-2`}
        style={{
          background: 'radial-gradient(ellipse 70% 55% at 80% 25%, rgba(255, 255, 255, 0.275) 0%, rgba(250, 250, 250, 0.2) 22%, rgba(220, 220, 220, 0.125) 42%, rgba(150, 150, 150, 0.06) 62%, rgba(60, 60, 60, 0.025) 82%, transparent 100%), radial-gradient(ellipse 55% 70% at 70% 15%, rgba(255, 255, 255, 0.175) 0%, transparent 55%)',
          filter: 'blur(1.5px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 25px rgba(255, 255, 255, 0.03)',
          transform: 'translate(23%, -28%)',
        }}
      />
      <div className={`wave-corner wave-top-right-3 absolute top-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-2`}
        style={{
          background: 'radial-gradient(ellipse 65% 50% at 82% 22%, rgba(255, 255, 255, 0.225) 0%, rgba(245, 245, 245, 0.15) 28%, rgba(200, 200, 200, 0.09) 48%, rgba(110, 110, 110, 0.04) 68%, rgba(30, 30, 30, 0.015) 88%, transparent 100%)',
          filter: 'blur(2px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translate(21%, -26%)',
        }}
      />

      {/* Bottom Corners */}
      <div className={`wave-corner wave-bottom-left-1 absolute bottom-0 left-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-3`}
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 15% 80%, rgba(255, 255, 255, 0.325) 0%, rgba(255, 255, 255, 0.225) 18%, rgba(240, 240, 240, 0.15) 35%, rgba(180, 180, 180, 0.09) 50%, rgba(100, 100, 100, 0.04) 65%, rgba(40, 40, 40, 0.015) 80%, transparent 100%), radial-gradient(ellipse 60% 80% at 25% 90%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
          filter: 'blur(1px)',
          boxShadow: '0 0 40px rgba(255, 255, 255, 0.075), inset 0 0 30px rgba(255, 255, 255, 0.04)',
          transform: 'translate(-25%, 30%)',
        }}
      />
      <div className={`wave-corner wave-bottom-left-2 absolute bottom-0 left-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-3`}
        style={{
          background: 'radial-gradient(ellipse 70% 55% at 20% 75%, rgba(255, 255, 255, 0.275) 0%, rgba(250, 250, 250, 0.2) 22%, rgba(220, 220, 220, 0.125) 42%, rgba(150, 150, 150, 0.06) 62%, rgba(60, 60, 60, 0.025) 82%, transparent 100%), radial-gradient(ellipse 55% 70% at 30% 85%, rgba(255, 255, 255, 0.175) 0%, transparent 55%)',
          filter: 'blur(1.5px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 25px rgba(255, 255, 255, 0.03)',
          transform: 'translate(-23%, 28%)',
        }}
      />
      <div className={`wave-corner wave-bottom-left-3 absolute bottom-0 left-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-3`}
        style={{
          background: 'radial-gradient(ellipse 65% 50% at 18% 78%, rgba(255, 255, 255, 0.225) 0%, rgba(245, 245, 245, 0.15) 28%, rgba(200, 200, 200, 0.09) 48%, rgba(110, 110, 110, 0.04) 68%, rgba(30, 30, 30, 0.015) 88%, transparent 100%)',
          filter: 'blur(2px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translate(-21%, 26%)',
        }}
      />

      <div className={`wave-corner wave-bottom-right-1 absolute bottom-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-4`}
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 85% 80%, rgba(255, 255, 255, 0.325) 0%, rgba(255, 255, 255, 0.225) 18%, rgba(240, 240, 240, 0.15) 35%, rgba(180, 180, 180, 0.09) 50%, rgba(100, 100, 100, 0.04) 65%, rgba(40, 40, 40, 0.015) 80%, transparent 100%), radial-gradient(ellipse 60% 80% at 75% 90%, rgba(255, 255, 255, 0.2) 0%, transparent 50%)',
          filter: 'blur(1px)',
          boxShadow: '0 0 40px rgba(255, 255, 255, 0.075), inset 0 0 30px rgba(255, 255, 255, 0.04)',
          transform: 'translate(25%, 30%)',
        }}
      />
      <div className={`wave-corner wave-bottom-right-2 absolute bottom-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-4`}
        style={{
          background: 'radial-gradient(ellipse 70% 55% at 80% 75%, rgba(255, 255, 255, 0.275) 0%, rgba(250, 250, 250, 0.2) 22%, rgba(220, 220, 220, 0.125) 42%, rgba(150, 150, 150, 0.06) 62%, rgba(60, 60, 60, 0.025) 82%, transparent 100%), radial-gradient(ellipse 55% 70% at 70% 85%, rgba(255, 255, 255, 0.175) 0%, transparent 55%)',
          filter: 'blur(1.5px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 25px rgba(255, 255, 255, 0.03)',
          transform: 'translate(23%, 28%)',
        }}
      />
      <div className={`wave-corner wave-bottom-right-3 absolute bottom-0 right-0 w-[600px] h-[600px] ${opacityClass} will-change-transform animate-blob-float-4`}
        style={{
          background: 'radial-gradient(ellipse 65% 50% at 82% 78%, rgba(255, 255, 255, 0.225) 0%, rgba(245, 245, 245, 0.15) 28%, rgba(200, 200, 200, 0.09) 48%, rgba(110, 110, 110, 0.04) 68%, rgba(30, 30, 30, 0.015) 88%, transparent 100%)',
          filter: 'blur(2px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translate(21%, 26%)',
        }}
      />

      {/* Sides */}
      <div className={`wave-side wave-left-1 absolute left-0 top-1/2 w-[500px] h-[800px] -translate-y-1/2 ${opacityClass} will-change-transform animate-blob-float-5`}
        style={{
          background: 'radial-gradient(ellipse 50% 70% at 10% 30%, rgba(255, 255, 255, 0.275) 0%, rgba(245, 245, 245, 0.19) 20%, rgba(200, 200, 200, 0.11) 40%, rgba(120, 120, 120, 0.05) 60%, rgba(40, 40, 40, 0.02) 80%, transparent 100%), radial-gradient(ellipse 60% 50% at 15% 70%, rgba(255, 255, 255, 0.2) 0%, transparent 55%)',
          filter: 'blur(1.2px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 20px rgba(255, 255, 255, 0.03)',
          transform: 'translateY(-50%) translateX(-20%)',
        }}
      />
      <div className={`wave-side wave-left-2 absolute left-0 top-1/2 w-[500px] h-[800px] -translate-y-1/2 ${opacityClass} will-change-transform animate-blob-float-5`}
        style={{
          background: 'radial-gradient(ellipse 45% 65% at 12% 50%, rgba(255, 255, 255, 0.225) 0%, rgba(235, 235, 235, 0.15) 25%, rgba(180, 180, 180, 0.08) 50%, rgba(90, 90, 90, 0.035) 75%, transparent 100%)',
          filter: 'blur(1.8px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translateY(-50%) translateX(-18%)',
        }}
      />

      <div className={`wave-side wave-right-1 absolute right-0 top-1/2 w-[500px] h-[800px] -translate-y-1/2 ${opacityClass} will-change-transform animate-blob-float-6`}
        style={{
          background: 'radial-gradient(ellipse 50% 70% at 90% 30%, rgba(255, 255, 255, 0.275) 0%, rgba(245, 245, 245, 0.19) 20%, rgba(200, 200, 200, 0.11) 40%, rgba(120, 120, 120, 0.05) 60%, rgba(40, 40, 40, 0.02) 80%, transparent 100%), radial-gradient(ellipse 60% 50% at 85% 70%, rgba(255, 255, 255, 0.2) 0%, transparent 55%)',
          filter: 'blur(1.2px)',
          boxShadow: '0 0 30px rgba(255, 255, 255, 0.06), inset 0 0 20px rgba(255, 255, 255, 0.03)',
          transform: 'translateY(-50%) translateX(20%)',
        }}
      />
      <div className={`wave-side wave-right-2 absolute right-0 top-1/2 w-[500px] h-[800px] -translate-y-1/2 ${opacityClass} will-change-transform animate-blob-float-6`}
        style={{
          background: 'radial-gradient(ellipse 45% 65% at 88% 50%, rgba(255, 255, 255, 0.225) 0%, rgba(235, 235, 235, 0.15) 25%, rgba(180, 180, 180, 0.08) 50%, rgba(90, 90, 90, 0.035) 75%, transparent 100%)',
          filter: 'blur(1.8px)',
          boxShadow: '0 0 25px rgba(255, 255, 255, 0.05), inset 0 0 18px rgba(255, 255, 255, 0.025)',
          transform: 'translateY(-50%) translateX(18%)',
        }}
      />
      </div>
    </div>
  );
};

export default WaveBackground;

